import { Suspense } from 'react';
import {
  ScrollToTop,
  ThemeProvider,
  CookieProvider,
  CookieConsent,
  ErrorBoundary,
  Loading,
  useCookieConsent,
} from '@xanectech/components/shared';

import { TooltipProvider, Toaster } from '@xanectech/ui/components';

import { BrowserRouter, useLocation } from 'react-router-dom';
import { AboutLogger, SubscriptionProvider } from '@xanectech/data';

import { TrueIDRoutes } from './AppRoutes';
import { AuthProvider, useAuth } from '@xanectech/auth';
import { UnlockDialogProvider } from './contexts/UnlockDialogContext';
import { EncryptionProvider } from '@xanectech/encryption';
import { supabase } from '@xanectech/supabase-client';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

import { createLogger, isFeatureEnabled } from '@xanectech/lib';
import React from 'react';

const logger = createLogger('TrueID.Locker - App');

const ScrollToTopHelper = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Loading fallback component
const RouteLoadingFallback = () => <Loading spinner='Xanec' />;

/**
 * Check if user has a master key in the database
 */
const checkHasMasterKey = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('public_keys')
      .select('id')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    logger.error('Failed to check master key:', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Conditionally renders Vercel Analytics and Speed Insights based on analytics
 * consent. Uses the consent context so re-renders automatically when consent changes.
 */
const AnalyticsGuard = () => {
  const { checkCategory } = useCookieConsent();
  if (!checkCategory('analytics')) return null;
  return (
    <>
      <Analytics />
      <SpeedInsights />
    </>
  );
};

/**
 * Inner app component that has access to auth context
 */
const AppWithEncryption = () => {
  const { user } = useAuth();
  // const helpdexUrl: string =
  //   (import.meta.env.VITE_HELPDEX_URL as string) ??
  //   'https://helpdex.xanectech.com';
  // const isHelpIntegrationEnabled = isFeatureEnabled(
  //   'TRUEID_LOCKER_HELPDESK_INTEGRATION',
  // );

  return (
    <EncryptionProvider
      userId={user?.id ?? null}
      checkHasMasterKey={
        user?.id ? () => checkHasMasterKey(String(user.id)) : undefined
      }
      autoPrompt={false}
    >
      <UnlockDialogProvider>
        <CookieProvider appName='TrueID.Locker'>
          <ErrorBoundary>
            <Suspense fallback={<RouteLoadingFallback />}>
              <TooltipProvider delayDuration={500} skipDelayDuration={200}>
                <Toaster />
                <CookieConsent />
                <AnalyticsGuard />
                <BrowserRouter>
                  {/* {isHelpIntegrationEnabled ? (
                    <HelpProvider app='trueid.locker' helpdexUrl={helpdexUrl}>
                      <ScrollToTopHelper />
                      <AboutLogger appName='TrueID.Locker' />
                      <TrueIDRoutes />
                      <HelpWidget />
                    </HelpProvider>
                  ) : ( */}
                  <>
                    <ScrollToTopHelper />
                    <AboutLogger appName='TrueID.Locker' />
                    <TrueIDRoutes />
                  </>
                  {/* )} */}
                </BrowserRouter>
              </TooltipProvider>
            </Suspense>
          </ErrorBoundary>
        </CookieProvider>
      </UnlockDialogProvider>
    </EncryptionProvider>
  );
};

const App = () => {
  return (
    <ThemeProvider defaultTheme='light' storageKey='xanectech-user-theme'>
      <AuthProvider>
        <SubscriptionProvider>
          <AppWithEncryption />
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
